import { Container, Sprite, type Ticker } from 'pixi.js';
import { AssetLoader } from '../asset/AssetLoader';
import { DefaultRandom, type Random } from '../core/Random';

/**
 * Top-down gold-coin rain. Spawns coin sprites at random x positions just
 * above the visible area and lets them fall under gravity with a tumble
 * rotation. Producer brief 2026-06-04 — JACKPOT tier should rain coins; the
 * existing {@link WinParticles} bursts give the right colour energy but
 * don't read as "coins" the way actual coin sprites do.
 *
 * <p>Distinct from {@link WinParticles} on three axes:
 * <ul>
 *   <li>Particles are SPRITES (commissioned coin texture), not Graphics
 *       circles — they read as discrete objects rather than abstract dots.
 *   <li>Emission is over TIME (sustained shower) rather than instant
 *       (single burst) — keeps the screen alive for the full
 *       celebration hold.
 *   <li>Spawn happens at the TOP of the area not the centre — coins fall
 *       through the entire field rather than radiating from one point.
 * </ul>
 *
 * <p>Self-cleans: when the spawn window closes and the last coin falls
 * past the cleanup line, the per-frame handler unregisters from the ticker.
 */
export class CoinShower extends Container {
  private coins: CoinState[] = [];
  private tickHandler: ((tick: Ticker) => void) | null = null;
  /** Active emission spec — null when not currently spawning new coins.
   *  Existing in-flight coins keep falling until they exit the cleanup
   *  line; the next {@link rain} call replaces this spec. */
  private spawnState: SpawnState | null = null;

  constructor(
    private readonly ticker: Ticker,
    private readonly random: Random = new DefaultRandom(),
  ) {
    super();
  }

  /**
   * Start a shower over the given rectangle. Coins spawn along the top
   * edge ({@code y = topY}, random x in [{@code areaLeft}, {@code areaRight}])
   * and fall until they pass {@code cleanupY}. Emission rate is tuned
   * by {@code totalCoins} / {@code durationMs} — a 60-coin shower over
   * 2500ms gives roughly one coin per 40ms, dense enough to feel like
   * a continuous rain without being a wall of sprites.
   *
   * <p>Calling {@code rain} again while a shower is running just resets
   * the spawn state; in-flight coins keep falling.
   */
  rain(options: RainOptions): void {
    const coinSize = options.coinSize ?? 36;
    this.spawnState = {
      remainingCoins: options.totalCoins ?? 60,
      durationMs: options.durationMs ?? 2500,
      elapsedMs: 0,
      areaLeft: options.areaLeft,
      areaRight: options.areaRight,
      topY: options.topY,
      cleanupY: options.cleanupY,
      coinSize,
      minSpeed: options.minSpeed ?? 360,
      maxSpeed: options.maxSpeed ?? 620,
      gravity: options.gravity ?? 1100,
      spinRate: options.spinRate ?? 4, // radians/sec base
    };

    if (!this.tickHandler) {
      this.tickHandler = (tick) => this.advance(tick.deltaMS);
      this.ticker.add(this.tickHandler);
    }
  }

  destroyEffect(): void {
    if (this.tickHandler) {
      this.ticker.remove(this.tickHandler);
      this.tickHandler = null;
    }
    for (const c of this.coins) c.sprite.destroy();
    this.coins = [];
    this.spawnState = null;
  }

  private advance(deltaMs: number): void {
    const seconds = deltaMs / 1000;

    // ---- Spawn new coins on the schedule defined by spawnState. ----
    if (this.spawnState) {
      const s = this.spawnState;
      const totalCoins = s.remainingCoins + this.coinsSpawned(s);
      const targetProgress = Math.min(1, (s.elapsedMs + deltaMs) / s.durationMs);
      const targetSpawned = Math.floor(totalCoins * targetProgress);
      const currentlySpawned = this.coinsSpawned(s);
      const toSpawn = Math.max(0, targetSpawned - currentlySpawned);
      for (let i = 0; i < toSpawn && s.remainingCoins > 0; i++) {
        this.spawnOne(s);
        s.remainingCoins -= 1;
      }
      s.elapsedMs += deltaMs;
      if (s.elapsedMs >= s.durationMs && s.remainingCoins === 0) {
        this.spawnState = null;
      }
    }

    // ---- Tick existing coins. ----
    const next: CoinState[] = [];
    for (const c of this.coins) {
      c.vy += c.gravity * seconds;
      c.sprite.x += c.vx * seconds;
      c.sprite.y += c.vy * seconds;
      c.sprite.rotation += c.spin * seconds;
      // Subtle alpha fade as the coin nears the cleanup line so it
      // doesn't pop out of existence abruptly. Fade region = bottom
      // 80px of the fall path.
      if (c.cleanupY && c.sprite.y > c.cleanupY - 80) {
        const remaining = Math.max(0, c.cleanupY - c.sprite.y) / 80;
        c.sprite.alpha = remaining;
      }
      if (c.sprite.y > c.cleanupY) {
        c.sprite.destroy();
        continue;
      }
      next.push(c);
    }
    this.coins = next;

    // ---- Self-stop when nothing left to do. ----
    if (this.coins.length === 0 && !this.spawnState && this.tickHandler) {
      this.ticker.remove(this.tickHandler);
      this.tickHandler = null;
    }
  }

  /** Helper — count of coins already spawned in the current shower.
   *  Reverse-engineers it from {@code remainingCoins} so we don't need
   *  a separate counter that drifts on every call. */
  private coinsSpawned(s: SpawnState): number {
    // remainingCoins is decremented as we spawn — but we don't store
    // the original total. Workaround: pull it from the diff between
    // elapsed progress and remaining. For now just track via
    // `s.spawned` if needed — simpler to add the field.
    return s.spawned ?? 0;
  }

  private spawnOne(s: SpawnState): void {
    const texture = AssetLoader.shared.getTexture('shared:icon:coin');
    if (!texture) return; // coin asset not loaded — silently skip
    const sprite = new Sprite(texture);
    sprite.anchor.set(0.5);
    const scale = s.coinSize / texture.height;
    sprite.scale.set(scale);
    // Spawn position — random x across the area, just above topY so
    // the coin enters the visible field rather than appearing in the
    // middle of empty sky.
    sprite.x = s.areaLeft + this.random.nextFloat() * (s.areaRight - s.areaLeft);
    sprite.y = s.topY - this.random.nextFloat() * 60;
    // Slight initial horizontal drift so the rain doesn't feel
    // perfectly vertical.
    const drift = (this.random.nextFloat() - 0.5) * 80;
    // Initial downward velocity in the [minSpeed, maxSpeed] band.
    const vy = s.minSpeed + this.random.nextFloat() * (s.maxSpeed - s.minSpeed);
    // Rotation: random sign, random magnitude up to spinRate.
    const spin = (this.random.nextFloat() - 0.5) * 2 * s.spinRate;
    this.addChild(sprite);
    this.coins.push({
      sprite,
      vx: drift,
      vy,
      gravity: s.gravity,
      spin,
      cleanupY: s.cleanupY,
    });
    s.spawned = (s.spawned ?? 0) + 1;
  }
}

interface CoinState {
  sprite: Sprite;
  vx: number;
  vy: number;
  gravity: number;
  spin: number; // radians/sec
  cleanupY: number;
}

interface SpawnState {
  remainingCoins: number;
  spawned?: number;
  durationMs: number;
  elapsedMs: number;
  areaLeft: number;
  areaRight: number;
  topY: number;
  cleanupY: number;
  coinSize: number;
  minSpeed: number;
  maxSpeed: number;
  gravity: number;
  spinRate: number;
}

export interface RainOptions {
  /** Left edge of the spawn band (local x coordinate). */
  areaLeft: number;
  /** Right edge of the spawn band (local x coordinate). */
  areaRight: number;
  /** y position where coins appear (local coordinate). Random
   *  back-step of up to 60px above this so all coins don't enter
   *  exactly at the same line. */
  topY: number;
  /** y position past which coins fade and are destroyed. */
  cleanupY: number;
  /** Coin sprite height in pixels. Defaults to 36. */
  coinSize?: number;
  /** Total number of coins to spawn across the duration. Defaults
   *  to 60. */
  totalCoins?: number;
  /** Spawn window length. Coins keep falling after this — the spawn
   *  window only controls when NEW coins enter. Defaults to 2500ms. */
  durationMs?: number;
  /** Initial downward velocity band. */
  minSpeed?: number;
  maxSpeed?: number;
  /** Gravity acceleration (px/sec²). */
  gravity?: number;
  /** Maximum tumble rate (radians/sec). Actual rate is uniformly random
   *  in [-spinRate, +spinRate] per coin. Defaults to 4. */
  spinRate?: number;
}
